import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import type { CountryCode } from 'libphonenumber-js/max';
import { parsePhoneNumber } from 'libphonenumber-js/max';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import { CountryCodeEnum } from '@/types/regionTypes';
import useMemoizedFn from '@/utils/useMemoizedFn';

import { Box } from '@/components/Box';
import { Breadcrumb } from '@/components/Breadcrumb';
import { CheckboxField } from '@/components/CheckboxField';
import { Col } from '@/components/Col';
import {
  getTitleOptionByValue,
  TitleField,
} from '@/components/FormFields/TitleField';
import { FormProvider } from '@/components/FormProvider';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { ReCaptcha } from '@/components/ReCaptcha';
import { Select } from '@/components/Select';
import { SelectField } from '@/components/SelectField';
import { TextAreaField } from '@/components/TextAreaField';
import { Typography } from '@/components/Typography';
import type { Scalars } from '@/graphql/posManager/api';
import { useAppointmentReasonOptions } from '@/hooks/useAppointmentReasonOptions';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import type { StoreHit } from '@/integrations/algolia/types';
import { Default } from '@/layouts/Default';
import type { DateInfo, Schedule, Weekday } from '@/lib/boutique/helpers';
import { formatTimesOption } from '@/lib/boutique/helpers';
import {
  CONTENTSQUARE_MASKED,
  MAX_CHAR_PHONE,
  SALESFORCE_TEXT_MAX_LENGTH,
} from '@/lib/forms/const';
import { getNameSchema, getTitleSchema } from '@/lib/forms/schemas';
import { paths } from '@/lib/paths';
import {
  formatNationalByPhonePrefix,
  getExamplePhone,
  isValidPhoneNumber,
} from '@/lib/phone';
import { getPhonePrefixSelectOpts } from '@/lib/phonePrefixes/helpers';
import { getRegionFromLocale } from '@/lib/regions';
import {
  commonMessages as appointmentMessages,
  ConfirmLegal,
  KeepInformed,
} from '@/messages/appointment';
import { commonMessages } from '@/messages/common';
import { formMessages, PersonalDataPolicy } from '@/messages/forms';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';
import type {
  RegionCityOption,
  RetailerOption,
} from '@/views/AppointmentView/helpers';
import {
  createAppointment,
  getNextAvailableDate,
  getOptsFromCountryPage,
  getUserPhone,
  getUserPhonePrefix,
  getUserPhonePrefixLabel,
  handleCalendarMonthChangeUtil,
  makeCityOptions,
  makeRegionOptions,
  resolveUnavailableAppointmentWeekdays,
  useBoutiqueSelect,
  useCalendarMonthTranslation,
} from '@/views/AppointmentView/helpers';

import { getNameFieldOrder, SortedNameFields } from '../SignUpView/helper';
import { CalendarBase } from '../WatchView/components/CalendarBase';

import { ReactComponent as GroupSVG } from '@/assets/icons/group-icon.svg';

import * as S from './AppointmentView.styles';

interface AppointmentViewProps {
  resultId?: string;
  store?: StoreHit;
}

export const AppointmentView = ({ resultId, store }: AppointmentViewProps) => {
  const today = useMemo(() => new Date(), []);
  const tomorrow = useMemo(() => {
    const d = new Date(today);
    d.setDate(today.getDate() + 1);
    return d;
  }, [today]);

  const { formatMessage } = useIntl();
  const {
    country,
    locale: regionProviderLocale,
    query: { languageCode },
  } = useRegionProvider();
  const isJapan = country.code === CountryCodeEnum.JP;
  const schema = yup.object().shape({
    phone: yup
      .string()
      .required(formatMessage(appointmentMessages.requirePhone))
      .max(MAX_CHAR_PHONE, formatMessage(appointmentMessages.maxPhoneCharMsg)),
    consent: yup
      .bool()
      .default(false)
      .oneOf([true], formatMessage(formMessages.requiredField)),
    newsletterOptIn: yup.bool().default(false),
    firstName: getNameSchema(formatMessage, 'first'),
    lastName: getNameSchema(formatMessage, 'last'),
    ...(isJapan && {
      firstNameKanji: getNameSchema(formatMessage, 'firstKanji'),
      lastNameKanji: getNameSchema(formatMessage, 'lastKanji'),
    }),
    email: yup
      .string()
      .email(formatMessage(appointmentMessages.invalidEmail))
      .required(formatMessage(appointmentMessages.requireEmail))
      .default(''),
    phonePrefix: yup.object(),
    reason: yup.object().default({
      label: formatMessage(appointmentMessages.discover_and_try),
      value: 'discover_and_try',
    }),
    timeSlot: yup
      .object()
      .required(formatMessage(appointmentMessages.requireField)),
    title: getTitleSchema(formatMessage, country.code),
    region: yup.object(),
    city: yup.object().nullable(),
    retailer: yup.object().nullable(),
    comments: yup
      .string()
      .max(
        SALESFORCE_TEXT_MAX_LENGTH,
        formatMessage(appointmentMessages.maxCommentsMsg),
      )
      .default(''),
    captcha: yup
      .bool()
      .default(false)
      .oneOf([true], formatMessage(appointmentMessages.requireField)),
  });
  const { locale, push, query } = useRouter();
  const {
    locale: { sfCode, slug: localeSlug },
  } = getRegionFromLocale(locale);
  const searchStores = useMemoizedFn(async () => {
    const params = new URLSearchParams();
    if (query.country) params.set('countryCode', String(query.country));
    params.set('localeSlug', String(localeSlug));
    params.set('offersAppointment', 'true');
    if (resultId) params.set('storeId', String(resultId));

    const res = await fetch(`/api/stores/search/?${params.toString()}`);
    if (!res.ok) throw new Error('Failed to fetch stores');

    return (await res.json()) as StoreHit[];
  });
  const [bannerImageUrl, setBannerImageUrl] = useState<string>('');
  const [serializedImageUrl, setSerializedImageUrl] = useState<string>('');
  const [storeName, setStoreName] = useState<string>('');
  const [isStorePage, setIsStorePage] = useState(false);
  const [utc_offset, setUtc_offset] = useState('');
  const [holidayHours, setHolidayHours] = useState<DateInfo[] | undefined>();
  const [reopenDate, setReopenDate] = useState<string | undefined>();
  const [nonWorkingDays, setNonWorkingDays] = useState<string[] | undefined>();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [date, setDate] = useState<string>(
    dayjs(tomorrow).format('YYYY-MM-DD'),
  );
  const [appointments, setAppointments] = useState<Schedule>();
  const [availableTimes, setAvailableTimes] = useState([
    { label: '11:00 - 12:00', value: '11:00 - 12:00' },
    { label: '12:00 - 13:00', value: '12:00 - 13:00' },
    { label: '13:00 - 14:00', value: '13:00 - 14:00' },
    { label: '14:00 - 15:00', value: '14:00 - 15:00' },
    { label: '15:00 - 16:00', value: '15:00 - 16:00' },
  ]);
  const [examplePhone, setExamplePhone] = useState<string>('');
  const [activeStartMonth, setActiveStartMonth] = useState(
    tomorrow.getMonth() + 1,
  );
  const [unavailableAppointmentWeekdays, setUnavailableAppointmentWeekdays] =
    useState<Weekday[] | undefined>();

  const {
    boutiqueSelectState,
    handleChangeBoutique,
    handleChangeCity,
    handleChangeRegion,
    setBoutiqueSelectState,
  } = useBoutiqueSelect(localeSlug);

  useCalendarMonthTranslation(activeStartMonth, regionProviderLocale.sfCode);
  const formCtx = useForm({
    resolver: yupResolver(schema),
  });
  const captcha = formCtx.watch('captcha');
  const {
    snapshot: { user },
  } = useUser();
  const { sendGenerateLeadEvent } = useGoogleAnalytics();
  const [isVerifyingRecaptcha, setIsVerifyingRecaptchaStatus] = useState(false);
  useEffect(() => {
    if (user) {
      formCtx.reset(user);
      const personalTitle =
        user?.metadata?.find((item) => item.key === 'user.personalTitle')
          ?.value ?? '';
      const titleOption = getTitleOptionByValue(
        formatMessage,
        country.code,
        personalTitle,
      );
      formCtx.setValue('title', titleOption);
    } else {
      formCtx.setValue('title', null);
    }
  }, [user]);
  useEffect(() => {
    const userPhone = getUserPhone(user);
    const userPhonePrefixValue = getUserPhonePrefix(user, country.code);
    const userPhonePrefixLabel = getUserPhonePrefixLabel(
      userPhonePrefixValue,
      userPhone,
      country.code,
    );

    const phonePrefixOption = {
      label: userPhonePrefixLabel,
      value: userPhonePrefixValue,
      code: country.code,
    };

    formCtx.setValue('phonePrefix', phonePrefixOption);
    if (userPhone) {
      const fullPhoneNumber = userPhonePrefixValue + userPhone;
      const phoneNumber = parsePhoneNumber(fullPhoneNumber);
      formCtx.setValue(
        'phone',
        formatNationalByPhonePrefix(
          userPhonePrefixValue,
          phoneNumber.formatNational(),
        ),
      );
    }
  }, [user, formCtx]);
  useEffect(() => {
    const dataPhone = formCtx.watch('phone') as Scalars['JSON']['output'];
    const dataPhonePrefix = formCtx.watch(
      'phonePrefix',
    ) as Scalars['JSON']['output'];
    const fullPhoneNumber = dataPhonePrefix.value + dataPhone;
    const isValidPhone = isValidPhoneNumber(fullPhoneNumber);
    if (isValidPhone) {
      const phoneNumber = parsePhoneNumber(fullPhoneNumber);
      formCtx.setValue(
        'phone',
        formatNationalByPhonePrefix(
          dataPhonePrefix.value,
          phoneNumber?.formatNational(),
        ),
      );
    }
    const phoneNumber = getExamplePhone(dataPhonePrefix.code as CountryCode);
    if (phoneNumber) setExamplePhone(phoneNumber);
  }, [formCtx.watch('phone'), formCtx.watch('phonePrefix')]);

  const initBoutiqueData = useMemoizedFn(async () => {
    if (query.storeslug && query.country) {
      // Prefer server-fetched store if available to avoid client search
      if (store) {
        try {
          const opts = getOptsFromCountryPage([store], localeSlug);
          const first = opts[0];
          setHolidayHours(
            first?.data?.temporary_hours?.holiday_hours ?? undefined,
          );
          setReopenDate(first?.data?.temporary_hours?.reopen_date ?? undefined);
          setAppointments(first?.data?.appointments as Schedule);
          setUtc_offset(first?.data?.utc_offset as string);
          const imageUrl =
            opts.find((opt) => opt.data?.photo_gallery?.[0]?.image?.url)?.data
              ?.photo_gallery?.[0]?.image?.url ?? '';
          setBannerImageUrl(imageUrl);
          setSerializedImageUrl(imageUrl);
          setUnavailableAppointmentWeekdays(
            resolveUnavailableAppointmentWeekdays(store),
          );
          formatTimesOption(
            first?.data?.appointments as Schedule,
            tomorrow,
            first?.data?.temporary_hours?.holiday_hours ?? undefined,
            setAvailableTimes,
            setNonWorkingDays,
            reopenDate,
          );
          setStoreName(first?.label);
        } catch (e) {
          if (e instanceof Error) setError(e.message);
        }
        setIsStorePage(false);
        return;
      }

      try {
        const hits = await searchStores();
        const stores = () => {
          const hit = (hits as StoreHit[]).find(
            (hit) => hit.slug === query?.storeslug,
          );
          return hit ? [hit] : [];
        };
        const opts = getOptsFromCountryPage(stores(), localeSlug);
        const storeOpt = opts[0];
        setHolidayHours(
          storeOpt?.data?.temporary_hours?.holiday_hours ?? undefined,
        );
        setReopenDate(
          storeOpt?.data?.temporary_hours?.reopen_date ?? undefined,
        );
        setAppointments(storeOpt?.data?.appointments as Schedule);
        setUtc_offset(storeOpt?.data?.utc_offset as string);
        const imageUrl =
          opts.find((opt) => opt.data?.photo_gallery?.[0]?.image?.url)?.data
            ?.photo_gallery?.[0]?.image?.url ?? '';
        setBannerImageUrl(imageUrl);
        setSerializedImageUrl(imageUrl);
        setUnavailableAppointmentWeekdays(
          resolveUnavailableAppointmentWeekdays(storeOpt?.data),
        );
        formatTimesOption(
          storeOpt?.data?.appointments as Schedule,
          tomorrow,
          storeOpt?.data?.temporary_hours?.holiday_hours ?? undefined,
          setAvailableTimes,
          setNonWorkingDays,
          reopenDate,
        );
        setStoreName(storeOpt?.label);
      } catch (e) {
        if (e instanceof Error) setError(e.message);
      }
      setIsStorePage(false);
    } else if (!query?.storeslug && query?.country) {
      try {
        const hits = await searchStores();
        const regionOpts = makeRegionOptions(hits as StoreHit[], formatMessage);
        const cityOpts = makeCityOptions(hits as StoreHit[]);

        setBoutiqueSelectState({
          boutiqueList: hits as StoreHit[],
          regionOptions: regionOpts,
          cityOptions: cityOpts,
        });
        const imageUrl =
          regionOpts.find((opt) => opt.data?.imageUrl)?.data?.imageUrl ?? '';
        setBannerImageUrl(imageUrl);
        setIsStorePage(true);
      } catch (e) {
        if (e instanceof Error) setError(e.message);
        setBoutiqueSelectState({
          boutiqueList: [],
          regionOptions: [],
          cityOptions: [],
        });
      }
      setBoutiqueSelectState({
        boutique: null,
        region: null,
        city: null,
      });
    }
  });

  useEffect(() => {
    initBoutiqueData();
  }, [query.storeslug, query.country, country.code]);

  const setBoutique = (opt: RetailerOption) => {
    handleChangeBoutique(opt);
    if (opt?.data?.temporary_hours?.holiday_hours) {
      setHolidayHours(opt?.data.temporary_hours.holiday_hours);
    }
    opt?.label && setStoreName(opt.label);
    setAppointments((opt?.data?.appointments as Schedule) || null);
    setUtc_offset(opt?.data?.utc_offset ?? '');
    setSerializedImageUrl(opt?.data?.photo_gallery?.[0]?.image?.url ?? '');
    setUnavailableAppointmentWeekdays(
      resolveUnavailableAppointmentWeekdays(opt?.data),
    );
    formatTimesOption(
      opt?.data?.appointments as Schedule,
      date,
      opt?.data?.temporary_hours?.holiday_hours ?? undefined,
      setAvailableTimes,
      setNonWorkingDays,
      reopenDate,
    );
  };

  useEffect(() => {
    const nextAvailableDate = getNextAvailableDate(
      tomorrow,
      unavailableAppointmentWeekdays,
    );
    setDate(nextAvailableDate);
  }, [tomorrow, unavailableAppointmentWeekdays]);

  useEffect(() => {
    if (appointments) {
      const boutique = boutiqueSelectState.boutique;
      formatTimesOption(
        boutique?.data?.appointments as Schedule,
        date,
        holidayHours ?? undefined,
        setAvailableTimes,
        setNonWorkingDays,
        reopenDate,
      );
      if (query?.storeslug) {
        formatTimesOption(
          appointments,
          date,
          holidayHours ?? undefined,
          setAvailableTimes,
          setNonWorkingDays,
          reopenDate,
        );
      }
    }
  }, [date]);

  useEffect(() => {
    if (appointments && holidayHours) {
      formatTimesOption(
        appointments,
        date,
        holidayHours,
        setAvailableTimes,
        setNonWorkingDays,
        reopenDate,
      );
    }
  }, [appointments, holidayHours, date]);

  useEffect(() => {
    if (availableTimes) formCtx.setValue('timeSlot', availableTimes[0]);
  }, [availableTimes]);
  const handleMonthChange = handleCalendarMonthChangeUtil(setActiveStartMonth);
  const futureDate = new Date(tomorrow);
  futureDate.setDate(tomorrow.getDate() + 29);
  const handleSubmit = async (data: any) => {
    setProcessing(true);
    data = { ...data, date: date };
    const isoTime = `${date}T${data.timeSlot.value}:00`;
    const startDateTime = isoTime + utc_offset;
    const AppointmentData = {
      salutation: data.title?.value ?? '',
      phone: data.phonePrefix.value + data.phone.replace(/\D/g, ''),
      reason: data.reason.value,
      websiteCountryCode: country.code,
      languageCode: sfCode,
      locationId: resultId ?? boutiqueSelectState.boutique?.value,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      comments: data.comments,
      startDateTime: startDateTime,
      newsletterOptIn: data.newsletterOptIn,
      ...(!!data.firstNameKanji && { firstNameKanji: data.firstNameKanji }),
      ...(!!data.lastNameKanji && { lastNameKanji: data.lastNameKanji }),
    };

    const serializedData = encodeURIComponent(
      JSON.stringify({
        title: data.title?.value ?? '',
        firstName: data.firstName,
        lastName: data.lastName,
        imageUrl: serializedImageUrl,
        storeName: storeName,
        countryCode: country.code,
      }),
    );
    try {
      const isValidPhone = isValidPhoneNumber(AppointmentData.phone);
      if (!isValidPhone) {
        setError(
          formatMessage(appointmentMessages.btqAppointmentPhoneNumberError),
        );
        setProcessing(false);
        return;
      }
      const appointmentRes = await createAppointment(AppointmentData);
      if (appointmentRes.success) {
        sendGenerateLeadEvent({
          appointmentId: appointmentRes.appointmentId,
          appointmentMotive: AppointmentData.reason,
          dateAppointment: AppointmentData.startDateTime,
          storeId: AppointmentData.locationId ?? '',
        });
        setProcessing(false);
        push({
          pathname: `/stores/appointment/confirm`,
          query: { data: serializedData },
        });
      } else {
        setProcessing(false);
        setError(appointmentRes.type);
      }
    } catch (error: any) {
      setProcessing(false);
      setError(error.message);
    }
  };
  const onChange = (newDate: any) => {
    if (newDate instanceof Date) {
      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, '0');
      const day = String(newDate.getDate()).padStart(2, '0');
      setDate(`${year}-${month}-${day}`);
    } else {
      setDate(dayjs(newDate).format('YYYY-MM-DD'));
    }
  };
  const handleChangeStoreCta = () => {
    push(
      paths.stores.appointment.asPath({
        country: query?.country as string,
      }),
    );
  };
  const { defaultOption, options: appointmentReasonOptions } =
    useAppointmentReasonOptions();

  return (
    <>
      <Box>
        <Breadcrumb.Wrapper>
          <Breadcrumb
            items={[
              {
                label: formatMessage(commonMessages.home),
                target: paths.home.url(),
              },
              {
                label: formatMessage(commonMessages.bookAppointment),
                target: paths.stores.appointment.asPath({
                  country: query?.country as string,
                }),
              },
            ]}
          />
        </Breadcrumb.Wrapper>
        {bannerImageUrl && (
          <S.BannerImageContainer>
            <S.BannerImageWapper>
              <S.BannerImage
                alt='store'
                height={0}
                loaderHeight={330}
                loading='eager'
                sizes='100vw'
                src={bannerImageUrl}
                width={0}
              />
            </S.BannerImageWapper>
            <S.BannerContent>
              {isStorePage === false && (
                <S.BannerContentWrapper>
                  <S.StoreName>{storeName}</S.StoreName>
                  <S.ChangeStoreCtaWrapper>
                    {storeName && (
                      <LegacyButton
                        colorVariant='white'
                        data-testid='changeStoreCta'
                        isUpperCase={false}
                        label={formatMessage(
                          appointmentMessages.changeStoreButtonText,
                        )}
                        onClick={handleChangeStoreCta}
                        variant='link'
                      >
                        <S.Chevron />
                      </LegacyButton>
                    )}
                  </S.ChangeStoreCtaWrapper>
                </S.BannerContentWrapper>
              )}
            </S.BannerContent>
          </S.BannerImageContainer>
        )}
        <Box bg='lightGrey' pt={5}>
          <S.AppointmentHeader
            colorized={true}
            data-testid='sectionheader'
            sizeDesktop={50}
            sizeMobile={40}
            text={formatMessage(commonMessages.bookAppointment)}
            textAlign='center'
          />
        </Box>
        <S.GroupIconWrapper>
          <GroupSVG />
        </S.GroupIconWrapper>
        <Box bg='lightGrey'>
          <S.BookOpetionRow>
            <Col>
              <Typography color='default' mb={4} textAlign='center'>
                {formatMessage(appointmentMessages.personalAdvice)}
              </Typography>
            </Col>
          </S.BookOpetionRow>
        </Box>
        <Box bg='lightGrey'>
          <FormProvider
            name='appointment'
            {...formCtx}
            onSubmit={(data) => {
              handleSubmit(data);
            }}
          >
            <Box>
              <Box bg='white'>
                <S.RegionSelectionsRow
                  gapDensity='none'
                  showRegionSelections={isStorePage}
                >
                  <Col>
                    <Typography
                      color='secondary'
                      data-testid='chooseRetailerText'
                      fontWeight='bold'
                      mt={4}
                      scale='h5'
                      textAlign='center'
                    >
                      {formatMessage(appointmentMessages.chooseRetailer)}
                    </Typography>
                  </Col>
                  <Col style={{ marginTop: '10px' }}>
                    {boutiqueSelectState.regionOptions.length > 0 && (
                      <Select
                        data-testid='region'
                        onChange={(opt) => {
                          handleChangeRegion(opt as RegionCityOption);
                        }}
                        options={boutiqueSelectState.regionOptions}
                        placeholder={formatMessage(
                          appointmentMessages.selectRegionPlaceholder,
                        )}
                        value={boutiqueSelectState.region}
                      />
                    )}
                  </Col>
                  <Col>
                    <Select
                      data-testid='city'
                      onChange={(opt) => {
                        handleChangeCity(opt as RegionCityOption);
                      }}
                      options={boutiqueSelectState.cityOptions}
                      placeholder={formatMessage(
                        appointmentMessages.selectCityPlaceholder,
                      )}
                      value={boutiqueSelectState.city}
                    />
                  </Col>
                  <Col>
                    <Select
                      onChange={(opt) => {
                        setBoutique(opt as RetailerOption);
                      }}
                      options={boutiqueSelectState.boutiqueOptions}
                      placeholder={formatMessage(
                        appointmentMessages.selectBoutiquePlaceholder,
                      )}
                      value={boutiqueSelectState.boutique}
                    />
                  </Col>
                </S.RegionSelectionsRow>
                <S.Row gapDensity='none'>
                  <Col>
                    <Typography
                      color='secondary'
                      fontWeight='bold'
                      mt={4}
                      textAlign='center'
                    >
                      {formatMessage(appointmentMessages.appointmentDate)}
                    </Typography>
                  </Col>
                </S.Row>
              </Box>
            </Box>
            <S.CalendarWrapper>
              <CalendarBase
                futureDate={futureDate}
                handleMonthChange={handleMonthChange}
                nonWorkingDays={nonWorkingDays}
                onChange={onChange}
                tomorrow={tomorrow}
                unavailableAppointmentWeekdays={unavailableAppointmentWeekdays}
                value={date}
              />
            </S.CalendarWrapper>
            <S.SameDayNoticeWrapper>
              <Col>
                <Typography
                  color='default'
                  pb={4}
                  pt={2}
                  textAlign='left'
                  width={400}
                >
                  {formatMessage(appointmentMessages.contactBoutiqueText)}
                </Typography>
              </Col>
            </S.SameDayNoticeWrapper>
            <S.Row gapDensity='none'>
              <Col>
                <Typography
                  color='secondary'
                  fontWeight='bold'
                  mt={4}
                  textAlign='center'
                >
                  {formatMessage(appointmentMessages.timeSlotText)}
                </Typography>
              </Col>
              <Col>
                <SelectField
                  isDisabled={availableTimes.length === 0}
                  name='timeSlot'
                  options={availableTimes}
                  placeholder={formatMessage(commonMessages.select)}
                  required
                />
              </Col>
              <Col>
                <Typography mt={2} textAlign='left'>
                  {formatMessage(appointmentMessages.validAppointmentText)}
                </Typography>
              </Col>
            </S.Row>
            <S.Row gapDensity='none'>
              <Col>
                <Typography
                  color='secondary'
                  fontWeight='bold'
                  mt={4}
                  scale='h5'
                  textAlign='center'
                >
                  {formatMessage(appointmentMessages.personalInfo)}
                </Typography>
              </Col>
              <Col>
                <TitleField countryCode={country.code} />
              </Col>
            </S.Row>
            <S.InputRow gapDensity='none'>
              <SortedNameFields
                arr={[
                  {
                    component: (
                      <Col key='firstName'>
                        <InputField
                          className={CONTENTSQUARE_MASKED}
                          data-testid='firstname'
                          maxLength={40}
                          name='firstName'
                          placeholder={formatMessage(
                            appointmentMessages.firstNamePlaceHolder,
                          )}
                          required
                        />
                      </Col>
                    ),
                    order: getNameFieldOrder(
                      country,
                      regionProviderLocale,
                      'first',
                    ),
                  },
                  {
                    component: (
                      <Col key='lastName'>
                        <InputField
                          className={CONTENTSQUARE_MASKED}
                          data-testid='lastname'
                          maxLength={80}
                          name='lastName'
                          placeholder={formatMessage(
                            appointmentMessages.lastNamePlaceHolder,
                          )}
                          required
                        />
                      </Col>
                    ),
                    order: getNameFieldOrder(
                      country,
                      regionProviderLocale,
                      'last',
                    ),
                  },
                  ...(isJapan
                    ? [
                        {
                          component: (
                            <Col key='firstNameKanji'>
                              <InputField
                                className={CONTENTSQUARE_MASKED}
                                data-testid='firstNameKanji'
                                maxLength={20}
                                name='firstNameKanji'
                                placeholder={formatMessage(
                                  formMessages.firstNameKanji,
                                )}
                                required
                              />
                            </Col>
                          ),
                          order: getNameFieldOrder(
                            country,
                            regionProviderLocale,
                            'firstKanji',
                          ),
                        },
                        {
                          component: (
                            <Col key='lastNameKanji'>
                              <InputField
                                className={CONTENTSQUARE_MASKED}
                                data-testid='lastNameKanji'
                                maxLength={20}
                                name='lastNameKanji'
                                placeholder={formatMessage(
                                  formMessages.lastNameKanji,
                                )}
                                required
                              />
                            </Col>
                          ),
                          order: getNameFieldOrder(
                            country,
                            regionProviderLocale,
                            'lastKanji',
                          ),
                        },
                      ]
                    : []),
                ]}
              />
              <Col>
                <InputField
                  className={CONTENTSQUARE_MASKED}
                  data-testid='email'
                  maxLength={80}
                  name='email'
                  placeholder={formatMessage(
                    appointmentMessages.emailPlaceHolder,
                  )}
                  required
                />
              </Col>
              <Col>
                <SelectField
                  name='phonePrefix'
                  options={getPhonePrefixSelectOpts()}
                />
              </Col>
              <Col>
                <InputField
                  className={CONTENTSQUARE_MASKED}
                  data-testid='phone'
                  maxLength={40}
                  name='phone'
                  placeholder={formatMessage(
                    appointmentMessages.phonePlaceHolder,
                  )}
                  required
                />
                <Typography color='secondaryGrey' scale='p3' textAlign='right'>
                  {examplePhone}
                </Typography>
              </Col>
            </S.InputRow>
            <S.Row gapDensity='none'>
              <Col>
                <Typography
                  color='secondary'
                  fontWeight='bold'
                  mb={2}
                  mt={4}
                  scale='h5'
                  textAlign='center'
                >
                  {formatMessage(appointmentMessages.bookAppointmentTo)}
                </Typography>
              </Col>
              <Col>
                <SelectField
                  defaultValue={defaultOption}
                  name='reason'
                  options={appointmentReasonOptions}
                  placeholder={formatMessage(
                    appointmentMessages.reasonPlaceHolder,
                  )}
                />
              </Col>
            </S.Row>
            <S.TextRow>
              <S.TextAreaCol>
                <TextAreaField
                  maxLength={SALESFORCE_TEXT_MAX_LENGTH}
                  name='comments'
                  placeholder={formatMessage(
                    appointmentMessages.tellUsSomething,
                  )}
                  rows={5}
                />
              </S.TextAreaCol>
              <S.CheckBoxCol>
                <CheckboxField name='newsletterOptIn'>
                  <S.LegalTextWrapper>
                    <S.OptinOptionsText>{KeepInformed}</S.OptinOptionsText>
                    <br />
                    <S.OptinOptionsText>
                      {PersonalDataPolicy}
                    </S.OptinOptionsText>
                    <br />
                  </S.LegalTextWrapper>
                </CheckboxField>
                <CheckboxField name='consent'>
                  <S.LegalTextWrapper>
                    <S.OptinOptionsText>{ConfirmLegal}</S.OptinOptionsText>
                  </S.LegalTextWrapper>
                </CheckboxField>
              </S.CheckBoxCol>
            </S.TextRow>
            <S.Row gapDensity='none'>
              <S.RecaptChaCol>
                <ReCaptcha
                  languageCode={languageCode}
                  name='captcha'
                  onChange={setIsVerifyingRecaptchaStatus}
                />
              </S.RecaptChaCol>
              <S.AppointmentButtonCol>
                <LegacyButton
                  data-testid='buttonSign'
                  disabled={!captcha || isVerifyingRecaptcha}
                  isFluid
                  isProcessing={processing || isVerifyingRecaptcha}
                  type='submit'
                >
                  {formatMessage(commonMessages.bookAppointment)}
                </LegacyButton>
              </S.AppointmentButtonCol>
            </S.Row>
            {error && (
              <S.TextRow>
                <Col>
                  <Typography color='error' textAlign='center'>
                    {error}
                  </Typography>
                </Col>
              </S.TextRow>
            )}
          </FormProvider>
        </Box>
      </Box>
    </>
  );
};
AppointmentView.getLayout = (page: ReactElement) => (
  <Default cmsData={page.props.cmsDefaultLayoutData}>
    <DraftModeContext.Provider value={page.props.draftMode}>
      {page}
    </DraftModeContext.Provider>
  </Default>
);
